import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import ConnectionService from '../services/ConnectionService';
import { useSystemEvent } from './SystemEventContext';
import { useProcessContext } from './ProcessContext';

// Constants for configuration
const CONFIG = {
  CONNECTION: {
    RECONNECT_DELAY_BASE: 1000,    // Base delay for reconnect (ms)
    RECONNECT_DELAY_MAX: 30000,    // Maximum reconnect delay (ms)
    RECONNECT_BACKOFF_FACTOR: 1.5, // Backoff multiplier for each retry
    SUBSCRIPTION_INITIAL_DELAY: 5000, // Initial delay before first subscription (ms)
    SUBSCRIPTION_INTERVAL: 1000,   // Delay between subscriptions (ms)
    SERVER_SUBSCRIPTION_INTERVAL: 1000, // Delay between server subscriptions (ms)
    HEARTBEAT_CHECK_INTERVAL: 10000, // How often to check for missing heartbeats (ms)
    HEARTBEAT_PING_TIMEOUT: 30000, // Send ping if no message for this duration (ms)
    HEARTBEAT_DEAD_TIMEOUT: 180000 // Consider connection dead after this duration (ms)
  },
  TOPICS: {
    DEFAULT_SERVER_ID: 'local',    // Default server ID if none specified
    EVENT_TYPES: ['heartbeat', 'systemevent', 'process', 'log', 'service'] // All event types to subscribe to
  }
};

// WebSocket Message Parser - Handles various message formats and transformations
export class WebSocketMessageParser {
  constructor(logger) {
    this.logger = logger || console;
    
    // Define decoding strategies in order of preference
    this.decodingStrategies = [
      this.parseStandardJson,
      this.parseQuotedJson,
      this.parseEscapedJson,
      this.parseCleanedJson,
      this.parseJsonWithBOM,
      this.parseTextWithJsonInsideIt
    ];
  }
  
  /**
   * Analyze binary data format characteristics
   * @param {ArrayBuffer} binaryData - Binary data to analyze
   * @returns {Object} - Format analysis information
   */
  analyzeBinaryFormat(binaryData) {
    const bytes = new Uint8Array(binaryData);
    const info = {
      size: bytes.length,
      binaryPercentage: 0,
      format: 'unknown'
    };
    
    // Count binary bytes (control chars except common ones)
    let binaryCount = 0;
    
    // Check for common binary format headers
    if (bytes.length > 1) {
      if (bytes[0] === 0xEA) {
        info.format = 'ea-binary-format';
      } else if (bytes[0] === 0xCE || bytes[0] === 0xCF) {
        info.format = 'possible-messagepack';
      }
    }
    
    // Calculate percentage of binary (non-printable) characters
    for (let i = 0; i < bytes.length; i++) {
      const b = bytes[i];
      if ((b < 32 && ![9, 10, 13].includes(b)) || b === 127) {
        binaryCount++;
      }
    }
    
    info.binaryPercentage = Math.round((binaryCount / bytes.length) * 100);
    return info;
  }

  /**
   * Process binary data with multiple decoding strategies
   * @param {ArrayBuffer} binaryData - Binary data from WebSocket
   * @returns {Object|null} - Parsed JSON object or null if parsing failed
   */
  async parseBinaryData(binaryData) {
    try {
      // Check if this is a binary format with EA header (custom format)
      const bytes = new Uint8Array(binaryData);
      if (bytes.length > 8 && bytes[0] === 0xEA) {
        return this.parseEABinaryFormat(binaryData);
      }
      
      // Check if it's likely binary data (non-text)
      const formatInfo = this.analyzeBinaryFormat(binaryData);
      if (formatInfo.binaryPercentage > 30) {
        this.logger.warn('Unable to parse binary format data');
        return null;
      }
      
      // Try to parse as text/JSON
      const jsonString = new TextDecoder().decode(binaryData);
      
      // Try each decoding strategy
      for (let strategy of this.decodingStrategies) {
        try {
          const result = strategy.call(this, jsonString);
          if (result) return result;
        } catch (strategyErr) {
          // Continue to next strategy
        }
      }
      
      return null;
    } catch (err) {
      this.logger.error('Error processing binary data:', err);
      return null;
    }
  }
  
  /**
   * Process binary data with 0xEA header (custom binary protocol)
   * @param {ArrayBuffer} binaryData - Binary data starting with 0xEA
   * @returns {Object} - Parsed log event object 
   */
  parseEABinaryFormat(binaryData) {
    try {
      const bytes = new Uint8Array(binaryData);
      
      // Validate minimum buffer size required for valid message
      if (bytes.length < 16) {
        throw new Error(`Binary buffer too small: ${bytes.length} bytes (minimum 16 bytes required)`); 
      }
      
      // Check if this is an empty log event (common pattern with 0xEA header)
      // Empty events typically have specific byte patterns or small payload size
      const isEmptyEvent = bytes.length < 40 || 
        (bytes[2] === 0 && bytes[3] === 0 && bytes[4] === 0);
      
      if (isEmptyEvent) {
        return {
          event_name: 'Log',
          timestamp: Date.now(),
          server_id: 'system',
          Log: {
            timestamp: Date.now(),
            source: 'BinaryProtocol',
            server_id: 'system',
            level: 'debug',
            message: 'Empty binary log event received',
            id: `binary-empty-${Date.now()}-${Math.floor(Math.random() * 1000000)}`,
            log_path: '/binary_protocol',
            file_name: 'binary_protocol.log'
          }
        };
      }
      
      // Extract header fields
      const header = {
        messageType: bytes[0],      // 0xEA
        version: bytes[1],         // Version 
        messageCategory: bytes[2]   // Category or subtype
      };
      
      // Extract timestamp (64-bit value at offset 8)
      let timestamp = 0;
      let serverId = 'unknown';
      let priority = 'info';
      
      // Create a DataView to properly read multi-byte values
      const dataView = new DataView(binaryData);
      
      try {
        timestamp = Number(dataView.getBigUint64(8, true)); // true = little endian
        // Validate timestamp is reasonable
        const now = Date.now();
        const oneYearAgo = now - 365 * 24 * 60 * 60 * 1000;
        if (timestamp < oneYearAgo || timestamp > now + 60000) {
          // Reset to current time if timestamp is unreasonable
          timestamp = now;
        }
      } catch (e) {
        timestamp = Date.now();
      }
      
      if (bytes.length >= 24) {
        // More robust server ID extraction with better validation
        try {
          // Extract server ID starting at byte 16
          const idBytes = [];
          let validIdFound = false;
          
          for (let i = 16; i < Math.min(32, bytes.length); i++) {
            // Only accept alphanumeric, dots, dashes and underscores as valid server ID characters
            const b = bytes[i];
            if ((b >= 48 && b <= 57) ||  // 0-9
                (b >= 65 && b <= 90) ||  // A-Z
                (b >= 97 && b <= 122) || // a-z
                b === 45 || b === 46 || b === 95) { // -, ., _
              idBytes.push(b);
            } else if (b === 0 && idBytes.length > 0) {
              // Found null terminator after collecting some bytes
              validIdFound = true;
              break;
            }
          }
          
          if (validIdFound && idBytes.length >= 3) {
            // Convert bytes to a string
            const possibleId = String.fromCharCode(...idBytes).trim();
            
            // Final validation check - only accept server IDs matching a reasonable pattern
            if (/^[a-zA-Z0-9._-]{3,24}$/.test(possibleId)) {
              serverId = possibleId;
            }
          }
        } catch (e) {
          // Failed to extract server ID
        }
        
        // Map message category to log level
        const logLevels = ['debug', 'info', 'warn', 'error'];
        priority = logLevels[bytes[2] % logLevels.length] || 'info';
      }
      
      // Extract text fragments from the binary data with improved validation
      let fragments = [];
      let currentFragment = '';
      let inString = false;
      
      for (let i = 32; i < bytes.length; i++) {
        // Only accept ASCII printable characters
        if (bytes[i] >= 32 && bytes[i] <= 126) {
          currentFragment += String.fromCharCode(bytes[i]);
          inString = true;
        } else if (inString) {
          // We've finished collecting a string fragment
          // Only keep meaningful fragments (at least 3 chars) that look like text
          if (currentFragment.length >= 3 && /\w/.test(currentFragment)) {
            fragments.push(currentFragment);
          }
          currentFragment = '';
          inString = false;
        }
      }
      
      // Add final fragment if there is one
      if (currentFragment.length >= 3 && /\w/.test(currentFragment)) {
        fragments.push(currentFragment);
      }
      
      const payloadText = fragments.join(' ');
      
      // Generate a properly structured event for LogContext
      const logDetails = {
        timestamp: timestamp,
        source: serverId,
        server_id: serverId,
        level: priority,
        message: `Binary Protocol Message: ${payloadText || '[No text content]'}`,
        binary_header: {
          type: `0x${header.messageType.toString(16).toUpperCase()}`,
          version: header.version,
          category: header.messageCategory,
          size: bytes.length
        },
        details: {
          binary_fragments: fragments,
          timestamp_raw: timestamp,
          protocol_version: header.version
        },
        id: `binary-${Date.now()}-${Math.floor(Math.random() * 1000000)}`,
        log_path: '/binary_protocol',
        file_name: 'binary_protocol.log'
      };
      
      return {
        event_name: 'Log',
        timestamp: timestamp,
        server_id: serverId,
        Log: logDetails
      };
    } catch (err) {
      this.logger.error('Error parsing binary format:', err);
      
      // Return a valid error event with LogContext structure
      const errorLogDetails = {
        timestamp: Date.now(),
        source: 'BinaryProtocolError',
        server_id: 'system',
        level: 'error',
        message: `Failed to parse binary message: ${err.message}`,
        error: err.toString(),
        id: `binary-error-${Date.now()}-${Math.floor(Math.random() * 1000000)}`,
        log_path: '/binary_protocol',
        file_name: 'binary_protocol_error.log'
      };
      
      return {
        event_name: 'Log',
        timestamp: Date.now(),
        server_id: 'system',
        Log: errorLogDetails
      };
    }
  }

  /**
   * Log detailed hex dump of binary data for debugging
   * @param {ArrayBuffer} binaryData - Binary data to log
   */
  logBinaryHexDump(binaryData) {
    try {
      const bytes = new Uint8Array(binaryData);
      const maxBytes = Math.min(bytes.length, 128); // Limit to first 128 bytes
      
      // Create a formatted hex dump with ASCII representation
      let hexDump = '';
      let asciiDump = '';
      let hexLine = '';
      let asciiLine = '';
      
      for (let i = 0; i < maxBytes; i++) {
        const byte = bytes[i];
        const hex = byte.toString(16).padStart(2, '0');
        
        hexLine += hex + ' ';
        
        // For ASCII representation, show printable chars, others as dots
        if (byte >= 32 && byte <= 126) {
          asciiLine += String.fromCharCode(byte);
        } else {
          asciiLine += '.';
        }
        
        // Create line breaks every 16 bytes
        if ((i + 1) % 16 === 0 || i === maxBytes - 1) {
          // Pad the last line if needed
          while (hexLine.length < 48) {
            hexLine += '   ';
          }
          
          hexDump += `${(i - (i % 16)).toString(16).padStart(8, '0')}  ${hexLine} | ${asciiLine}\n`;
          hexLine = '';
          asciiLine = '';
        }
      }
      
      this.logger.log(`Binary hex dump (first ${maxBytes} of ${bytes.length} bytes):\n${hexDump}`);
      
      // Try different decodings to help diagnose the issue
      try {
        const utf8String = new TextDecoder('utf-8').decode(binaryData);
        this.logger.log('UTF-8 Attempt:', utf8String.substring(0, 100));
      } catch (e) { /* ignore */ }
      
      try {
        const utf16String = new TextDecoder('utf-16').decode(binaryData);
        this.logger.log('UTF-16 Attempt:', utf16String.substring(0, 100));
      } catch (e) { /* ignore */ }
      
    } catch (err) {
      this.logger.error('Error generating hex dump:', err);
    }
  }

  /**
   * Standard JSON parsing
   * @param {string} jsonString - JSON string to parse
   * @returns {Object|null} - Parsed object or null
   */
  parseStandardJson(jsonString) {
    return JSON.parse(jsonString);
  }

  /**
   * Remove outer quotes from double-encoded JSON
   * @param {string} jsonString - JSON string to parse
   * @returns {Object|null} - Parsed object or null
   */
  parseQuotedJson(jsonString) {
    // Check if string starts and ends with quotes
    if (jsonString.startsWith('"') && jsonString.endsWith('"')) {
      // Remove outer quotes and parse
      const cleanedString = jsonString.substring(1, jsonString.length - 1);
      return JSON.parse(cleanedString);
    }
    return null;
  }

  /**
   * Handle escaped quotes in double-encoded JSON
   * @param {string} jsonString - JSON string to parse
   * @returns {Object|null} - Parsed object or null
   */
  parseEscapedJson(jsonString) {
    // Remove outer quotes if present
    let cleanedString = jsonString;
    if (jsonString.startsWith('"') && jsonString.endsWith('"')) {
      cleanedString = jsonString.substring(1, jsonString.length - 1);
    }
    
    // Unescape quotes within string
    cleanedString = cleanedString.replace(/\\\\?"/g, '"');
    return JSON.parse(cleanedString);
  }

  /**
   * Attempt to repair malformed JSON
   * @param {string} jsonString - JSON string to parse
   * @returns {Object|null} - Parsed object or null
   */
  parseCleanedJson(jsonString) {
    // Attempt various repairs
    // 1. Try removing non-printable characters
    // eslint-disable-next-line no-control-regex
    const cleaned = jsonString.replace(/[\x00-\x1F\x7F-\x9F]/g, '');
    return JSON.parse(cleaned);
  }

  /**
   * Attempt to parse JSON with BOM
   * @param {string} jsonString - JSON string to parse
   * @returns {Object|null} - Parsed object or null
   */
  parseJsonWithBOM(jsonString) {
    // Check for BOM
    if (jsonString.startsWith('\ufeff')) {
      // Remove BOM and parse
      const cleanedString = jsonString.substring(1);
      return JSON.parse(cleanedString);
    }
    return null;
  }

  /**
   * Attempt to parse text with JSON inside it
   * @param {string} text - Text to parse
   * @returns {Object|null} - Parsed object or null
   */
  parseTextWithJsonInsideIt(text) {
    try {
      // Look for opening braces or brackets that might indicate JSON start
      let potentialStarts = [];
      for (let i = 0; i < text.length; i++) {
        if (text[i] === '{' || text[i] === '[') {
          potentialStarts.push(i);
          if (potentialStarts.length >= 5) break; // Limit to first few potential starts
        }
      }
      
      // Try to parse each potential JSON fragment
      if (potentialStarts.length > 0) {
        this.logger.log(`Found ${potentialStarts.length} potential JSON start positions`);
        
        for (const start of potentialStarts) {
          try {
            // Extract a substring starting at this position
            const fragment = text.substring(start);
            // Look for a potential closing bracket/brace
            let depth = 0;
            let end = -1;
            
            for (let i = 0; i < Math.min(fragment.length, 1000); i++) {
              if (fragment[i] === '{' || fragment[i] === '[') depth++;
              if (fragment[i] === '}' || fragment[i] === ']') {
                depth--;
                if (depth === 0) {
                  end = i + 1;
                  break;
                }
              }
            }
            
            if (end > 0) {
              const jsonFragment = fragment.substring(0, end);
              try {
                JSON.parse(jsonFragment);
                this.logger.log(`Found valid JSON at position ${start}:`, jsonFragment);
                break;
              } catch (e) {
                // Not valid JSON, continue
              }
            }
          } catch (e) {
            // Ignore errors during extraction attempts
          }
        }
      }
    } catch (err) {
      // Ignore any errors in the extraction logic
    }
    return null;
  }

  /**
   * Log detailed information about parsing failures for debugging
   * @param {string} jsonString - The string that failed to parse
   */
  logParsingFailure(jsonString) {
    const sample = jsonString.substring(0, 100);
    this.logger.warn('Failed to parse WebSocket data with all strategies. Sample:', sample);
    
    // Log character codes to debug invisible characters
    let charCodes = '';
    let hexCodes = '';
    for (let i = 0; i < Math.min(jsonString.length, 50); i++) {
      const code = jsonString.charCodeAt(i);
      charCodes += code + ' ';
      hexCodes += code.toString(16).padStart(2, '0') + ' ';
    }
    this.logger.warn('Character codes (decimal):', charCodes);
    this.logger.warn('Character codes (hex):', hexCodes);
    
    // Check for non-printable characters
    const nonPrintable = Array.from(jsonString.substring(0, 200))
      .filter(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) > 126)
      .map(char => char.charCodeAt(0));
    
    if (nonPrintable.length > 0) {
      this.logger.warn(`Found ${nonPrintable.length} non-printable characters:`, nonPrintable);
    }
    
    // Try to extract valid JSON substrings
    this.tryExtractValidJson(jsonString);
  }
  
  /**
   * Try to extract valid JSON substrings from a problematic string
   * @param {string} text - Text to analyze for JSON fragments
   */
  tryExtractValidJson(text) {
    try {
      // Look for opening braces or brackets that might indicate JSON start
      let potentialStarts = [];
      for (let i = 0; i < text.length; i++) {
        if (text[i] === '{' || text[i] === '[') {
          potentialStarts.push(i);
          if (potentialStarts.length >= 5) break; // Limit to first few potential starts
        }
      }
      
      // Try to parse each potential JSON fragment
      if (potentialStarts.length > 0) {
        this.logger.log(`Found ${potentialStarts.length} potential JSON start positions`);
        
        for (const start of potentialStarts) {
          try {
            // Extract a substring starting at this position
            const fragment = text.substring(start);
            // Look for a potential closing bracket/brace
            let depth = 0;
            let end = -1;
            
            for (let i = 0; i < Math.min(fragment.length, 1000); i++) {
              if (fragment[i] === '{' || fragment[i] === '[') depth++;
              if (fragment[i] === '}' || fragment[i] === ']') {
                depth--;
                if (depth === 0) {
                  end = i + 1;
                  break;
                }
              }
            }
            
            if (end > 0) {
              const jsonFragment = fragment.substring(0, end);
              try {
                JSON.parse(jsonFragment);
                this.logger.log(`Found valid JSON at position ${start}:`, jsonFragment);
                break;
              } catch (e) {
                // Not valid JSON, continue
              }
            }
          } catch (e) {
            // Ignore errors during extraction attempts
          }
        }
      }
    } catch (err) {
      // Ignore any errors in the extraction logic
    }
  }

  /**
   * Process text data directly as JSON
   * @param {string} textData - Text data from WebSocket
   * @returns {Object|null} - Parsed JSON object or null if parsing failed
   */
  parseTextData(textData) {
    try {
      return JSON.parse(textData);
    } catch (err) {
      // If direct parsing fails, try repair strategies
      for (let strategy of this.decodingStrategies.slice(1)) {
        try {
          const result = strategy.call(this, textData);
          if (result) {
            this.logger.log(`Successfully parsed text data with strategy: ${strategy.name}`);
            return result;
          }
        } catch (strategyErr) {
          // Continue to next strategy
        }
      }
      
      this.logParsingFailure(textData);
      return null;
    }
  }
}

// Utility functions
const WSUtils = {
  // Calculate exponential backoff delay (with jitter)
  calculateBackoff: (attempt, baseDelay = CONFIG.CONNECTION.RECONNECT_DELAY_BASE, maxDelay = CONFIG.CONNECTION.RECONNECT_DELAY_MAX, factor = CONFIG.CONNECTION.RECONNECT_BACKOFF_FACTOR) => {
    const delay = Math.min(baseDelay * Math.pow(factor, attempt), maxDelay);
    // Add jitter (±15%) to prevent reconnection storms
    return delay * (0.85 + Math.random() * 0.3);
  },
  
  // Deep equality check for objects (useful for preventing unnecessary re-renders/reconnects)
  deepEqual: (a, b) => {
    if (a === b) return true;
    if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;
    
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    
    if (keysA.length !== keysB.length) return false;
    
    for (const key of keysA) {
      if (!keysB.includes(key)) return false;
      if (!WSUtils.deepEqual(a[key], b[key])) return false;
    }
    
    return true;
  }
};

// WebSocket custom logger with consistent formatting - only keeping error logs
const WSLogger = {
  debug: () => {},  // No-op to prevent debug logs
  log: () => {},    // No-op to prevent logs
  info: () => {},   // No-op to prevent info logs
  warn: (message, ...data) => console.warn(`WebSocketContext: ${message}`, ...data), // Keep warnings
  error: (message, ...data) => console.error(`WebSocketContext: ${message}`, ...data), // Keep errors
  highlight: () => {} // No-op to prevent highlight logs
};

// WebSocket connection states
export const CONNECTION_STATUS = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  ERROR: 'error'
};

const WebSocketContext = createContext();

export function WebSocketProvider({ children }) {
  const { wsConnection, isAuthenticated, serverData } = useAuth();
  const { updateServerMetrics } = useSystemEvent();
  // Process context hook is available but not currently used
  useProcessContext();

  // Core state
  const [socket, setSocket] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(CONNECTION_STATUS.DISCONNECTED);
  const [lastMessage, setLastMessage] = useState(null);
  const [error, setError] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  
  // Refs for managing connection lifecycle
  const reconnectAttempt = useRef(0);
  const reconnectTimer = useRef(null);
  const heartbeatTimer = useRef(null);
  const subscriptionTimers = useRef([]);
  const lastMessageTime = useRef(Date.now());
  const intentionalClose = useRef(false);
  const subscriptionsRef = useRef(subscriptions);
  const hasSubscribed = useRef(false);
  
  // Message parser instance
  const messageParser = useRef(new WebSocketMessageParser(WSLogger));
  
  // Keep track of the latest data for use in cleanup functions
  const wsConnectionRef = useRef(wsConnection);
  const serverDataRef = useRef(serverData);
  
  useEffect(() => {
    wsConnectionRef.current = wsConnection;
    serverDataRef.current = serverData;
  }, [wsConnection, serverData]);

  useEffect(() => {
    subscriptionsRef.current = subscriptions;
  }, [subscriptions]);
  
  // State for server-specific messages organized by server ID
  const [serverMessages, setServerMessages] = useState({});
  
  // Function to clean up all timers and references
  const cleanupConnection = useCallback(() => {
    // Clear all timers
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    
    if (heartbeatTimer.current) {
      clearInterval(heartbeatTimer.current);
      heartbeatTimer.current = null;
    }
    
    // Clear all subscription timers
    subscriptionTimers.current.forEach(timer => clearTimeout(timer));
    subscriptionTimers.current = [];
  }, []);

  // Extract server ID from topic or message
  const extractServerId = useCallback((message) => {
    // Try to extract server ID from topic field first
    if (message.topic && typeof message.topic === 'string') {
      const topicParts = message.topic.split('_');
      if (topicParts.length >= 3) {
        return topicParts[0].toLowerCase();
      }
    }
    
    // Try to extract from server_id field
    if (message.server_id) {
      return message.server_id.toLowerCase();
    }
    
    // Try to extract from host_name field
    if (message.host_name) {
      return message.host_name.toLowerCase();
    }
    
    // For heartbeat events, try to extract from message structure
    if (message.event_name === 'Heartbeat' && message.server_id) {
      return message.server_id.toLowerCase();
    }
    
    // For process events
    if (message.event_name === 'Process' && message.server_id) {
      return message.server_id.toLowerCase();
    }
    
    // For system events
    if (message.event_name === 'SystemEvent' && message.server_id) {
      return message.server_id.toLowerCase();
    }
    
    // Default to 'local' if we can't determine server_id
    return CONFIG.TOPICS.DEFAULT_SERVER_ID;
  }, []);
  
  // Process incoming message and update state
  const processMessage = useCallback((message, messageType = 'unknown') => {
    try {
      // Determine event type from message
      let eventType = messageType;
      
      if (message.event_name) {
        if (message.event_name === 'Process') eventType = 'process';
        else if (message.event_name === 'SystemEvent') eventType = 'systemevent';
        else if (message.event_name === 'Log') eventType = 'log';
        else if (message.event_name === 'Heartbeat') eventType = 'heartbeat';
        else if (message.event_name === 'Service') eventType = 'service';
      }
      
      // Extract the server ID from the message
      const serverId = extractServerId(message);
      
      WSLogger.log(`Received ${eventType} event for server ${serverId}`);
      
      // Update latest event for the server
      setServerMessages(prev => ({
        ...prev,
        [serverId]: {
          ...prev[serverId],
          [eventType]: {
            timestamp: Date.now(),
            data: message
          }
        }
      }));
      
      // Also set the lastMessage for backward compatibility
      setLastMessage({ 
        type: eventType, 
        data: message,
        serverId: serverId
      });
      
      // Dispatch custom event for background processing
      // This ensures connection status is maintained even when the Dashboard component is not mounted
      const timestamp = Date.now();
      const eventDetail = {
        serverId,
        data: message,
        timestamp
      };
      
      // Dispatch an event based on the type
      if (eventType === 'heartbeat') {
        WSLogger.debug('Dispatching heartbeat-update event for background processing', { serverId, timestamp });
        window.dispatchEvent(new CustomEvent('heartbeat-update', { detail: eventDetail }));
      } else if (eventType === 'systemevent') {
        WSLogger.debug('Dispatching systemevent-update event for background processing', { serverId, timestamp });
        window.dispatchEvent(new CustomEvent('systemevent-update', { detail: eventDetail }));
      } else if (eventType === 'process') {
        WSLogger.debug('Dispatching process-update event for background processing', { serverId, timestamp });
        window.dispatchEvent(new CustomEvent('process-update', { detail: eventDetail }));
      } else if (eventType === 'log') {
        WSLogger.debug('Dispatching log-update event for background processing', { serverId, timestamp });
        window.dispatchEvent(new CustomEvent('log-update', { detail: eventDetail }));
      }
      
      // Dispatch custom event for real-time updates to components
      // This allows components to subscribe to specific event types
      // without polling or excessive re-renders
      if (eventType === 'systemevent') {
        const customEvent = new CustomEvent('systemevent-update', {
          detail: {
            serverId,
            data: message,
            timestamp: Date.now()
          }
        });
        window.dispatchEvent(customEvent);
      } else if (eventType === 'process') {
        const customEvent = new CustomEvent('process-update', {
          detail: {
            serverId,
            data: message,
            timestamp: Date.now()
          }
        });
        window.dispatchEvent(customEvent);
      } else if (eventType === 'log') {
        const customEvent = new CustomEvent('log-update', {
          detail: {
            serverId,
            data: message,
            timestamp: Date.now()
          }
        });
        window.dispatchEvent(customEvent);
      }
      
      return { eventType, serverId, message };
    } catch (err) {
      WSLogger.error('Error processing message:', err);
      return { eventType: 'error', error: err.message, message };
    }
  }, [extractServerId]);
  
  // Handle incoming messages - both binary and text
  const handleMessage = useCallback((event) => {
    // Update last message timestamp
    lastMessageTime.current = Date.now();
    
    if (event.data instanceof Blob) {
      // Binary message handling
      WSLogger.log('Processing binary Blob data');
      const reader = new FileReader();
      
      reader.onload = async () => {
        try {
          // Use the message parser for binary data
          const parsedMessage = await messageParser.current.parseBinaryData(reader.result);
          
          if (parsedMessage) {
            processMessage(parsedMessage, 'binary');
          } else {
            // If parsing failed completely, store raw data for debugging
            const rawString = new TextDecoder().decode(reader.result);
            setLastMessage({ 
              type: 'binary', 
              raw: rawString.substring(0, 500), // Limit length for display
              parseError: true 
            });
          }
        } catch (err) {
          WSLogger.error('Error processing binary data:', err);
          setLastMessage({ type: 'binary', error: err.message });
        }
      };
      
      reader.onerror = (err) => {
        WSLogger.error('Error reading binary data:', err);
        setLastMessage({ type: 'binary', error: 'Failed to read binary data' });
      };
      
      // Read the blob as an array buffer
      reader.readAsArrayBuffer(event.data);
    } else {
      // Text message handling
      try {
        WSLogger.log('Processing text data');
        // Use the message parser for text data
        const parsedMessage = messageParser.current.parseTextData(event.data);
        
        if (parsedMessage) {
          processMessage(parsedMessage, 'text');
        } else {
          WSLogger.warn('Failed to parse text message with all strategies');
          setLastMessage({ 
            type: 'text', 
            raw: event.data.substring(0, 500), // Limit length for display
            parseError: true 
          });
        }
      } catch (err) {
        WSLogger.warn('Failed to parse text message as JSON:', err);
        setLastMessage({ type: 'text', raw: event.data });
      }
    }
  }, [processMessage]);

  // Efficient function to subscribe to all topics for all servers at once
  const subscribeToAllTopics = useCallback((ws, servers) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      WSLogger.error('Cannot subscribe - WebSocket not connected');
      return false;
    }
    
    if (!servers || !servers.length) {
      WSLogger.error('No servers provided for subscription');
      return false;
    }
    
    try {
      // Create arrays to collect all topics and their full paths
      const allTopics = [];
      const allFullTopics = [];
      
      // Process all servers and collect their topics
      servers.forEach(server => {
        const serverId = server.server_id?.toLowerCase() || CONFIG.TOPICS.DEFAULT_SERVER_ID;
        const serverName = server.server_name || serverId;
        
        // Generate topic names for this server
        const serverTopics = CONFIG.TOPICS.EVENT_TYPES.map(eventType => `${serverId}_${eventType}_events`);
        
        // Add to our collection
        allTopics.push(...serverTopics);
        
        // Also track the full topic paths for our state
        const fullServerTopics = serverTopics.map(topic => `persistent://public/default/${topic}`);
        allFullTopics.push(...fullServerTopics);
        
        WSLogger.log(`Prepared subscription for server: ${serverName} (${serverId}) with ${serverTopics.length} topics`);
      });
      
      // Create a single subscription message with all topics and event types
      const subscriptionMessage = {
        request_type: 0,
        models: CONFIG.TOPICS.EVENT_TYPES, // List of all event types
        symbols: allTopics                // List of all topics
      };
      
      WSLogger.highlight(`Sending combined subscription for ${servers.length} server(s) with ${allTopics.length} topics`, subscriptionMessage);
      
      // Send a single subscription request for all topics and event types
      ws.send(JSON.stringify(subscriptionMessage));
      
      // Update subscriptions state with all topics
      setSubscriptions(prev => {
        // Add any topics that aren't already in the subscriptions list
        const newTopics = allFullTopics.filter(topic => !prev.includes(topic));
        if (newTopics.length > 0) {
          return [...prev, ...newTopics];
        }
        return prev;
      });

      // Optimistically dispatch 'server-connected' for each server
      servers.forEach(server => {
        const serverId = server.server_id?.toLowerCase() || CONFIG.TOPICS.DEFAULT_SERVER_ID;
        const serverName = server.server_name || serverId;
        const eventDetail = { serverId, serverName, timestamp: Date.now(), details: "Subscription message sent" };
        window.dispatchEvent(new CustomEvent('server-connected', { detail: eventDetail }));
        WSLogger.log(`WebSocketContext: Optimistically dispatched "server-connected" for ${serverName} (${serverId})`);
      });
      
      return true;
    } catch (err) {
      WSLogger.error(`Error subscribing to topics for ${servers.length} server(s):`, err);
      return false;
    }
  }, []);
  
  // Handle subscribing to topics for all servers - automatic subscription during connection
  const handleSubscriptions = useCallback((ws) => {
    // Clear any previous subscription timers
    if (subscriptionTimers.current.length > 0) {
      subscriptionTimers.current.forEach(timer => clearTimeout(timer));
      subscriptionTimers.current = [];
    }
    
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      WSLogger.error('Cannot subscribe - WebSocket not connected');
      return false;
    }
    
    // Skip if we've already subscribed in this session
    if (hasSubscribed.current) {
      WSLogger.log('Subscriptions already sent in this session, skipping');
      return true;
    }

    // Get server list from Auth context
    const servers = serverDataRef.current || [];
    
    // Mark that we've attempted subscription in this session
    hasSubscribed.current = true;
    
    // If no servers in the Auth context, use the default server ID
    if (!servers.length) {
      WSLogger.warn('No servers found in Auth context, using default server ID');
      const defaultServerId = CONFIG.TOPICS.DEFAULT_SERVER_ID;
      return subscribeToAllTopics(ws, [{ server_id: defaultServerId, server_name: defaultServerId }]);
    }
    
    WSLogger.highlight(`Found ${servers.length} server(s) to subscribe to`);
    
    // Use a single timer with a short delay before sending subscriptions
    const subscriptionTimer = setTimeout(() => {
      if (ws.readyState !== WebSocket.OPEN) {
        WSLogger.error('Cannot subscribe - WebSocket connection was closed');
        return false;
      }
      
      // Subscribe to all servers at once with a single function call
      return subscribeToAllTopics(ws, servers);
    }, CONFIG.CONNECTION.SUBSCRIPTION_INITIAL_DELAY);
    
    subscriptionTimers.current.push(subscriptionTimer);
    return true;
  }, [subscribeToAllTopics]);

  // Set up heartbeat monitoring
  const setupHeartbeatMonitoring = useCallback((ws) => {
    if (heartbeatTimer.current) {
      clearInterval(heartbeatTimer.current);
    }
    
    heartbeatTimer.current = setInterval(() => {
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      
      const currentTime = Date.now();
      const timeSinceLastMessage = currentTime - lastMessageTime.current;
      
      // Send ping if no message for a while
      if (timeSinceLastMessage > CONFIG.CONNECTION.HEARTBEAT_PING_TIMEOUT) {
        WSLogger.log(`No messages for ${Math.round(timeSinceLastMessage/1000)}s, sending ping`);
        try {
          ws.send(JSON.stringify({ request_type: 0, ping: 'heartbeat-check' }));
        } catch (err) {
          WSLogger.error('Failed to send ping:', err);
        }
      }
      
      // Consider connection dead after too long
      if (timeSinceLastMessage > CONFIG.CONNECTION.HEARTBEAT_DEAD_TIMEOUT) {
        WSLogger.error(`No messages for ${Math.round(timeSinceLastMessage/1000)}s, connection appears dead`);
        intentionalClose.current = true;
        try {
          ws.close(1000, 'Connection timeout - no messages received');
        } catch (err) {
          WSLogger.error('Error closing dead connection:', err);
        }
        setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
      }
    }, CONFIG.CONNECTION.HEARTBEAT_CHECK_INTERVAL);
  }, []);

  // Initialize a new WebSocket connection - defined first to avoid circular dependency
  const initializeConnection = useCallback(() => {
    if (!wsConnection || !isAuthenticated) return;
    
    try {
      setConnectionStatus(CONNECTION_STATUS.CONNECTING);
      intentionalClose.current = false;
      
      // Reset last message time
      lastMessageTime.current = Date.now();
      
      // Construct WebSocket URL
      const wsUrl = ConnectionService.buildWebSocketUrl(wsConnection);
      if (!wsUrl) {
        WSLogger.error('Failed to build WebSocket URL');
        setConnectionStatus(CONNECTION_STATUS.ERROR);
        setError('Failed to build WebSocket URL. Check your connection information.');
        return;
      }
      
      WSLogger.highlight(`Connecting to ${wsUrl}`);
      const ws = new WebSocket(wsUrl);
      
      // Setup event handlers
      ws.onopen = () => {
        WSLogger.highlight('Connected successfully');
        setConnectionStatus(CONNECTION_STATUS.CONNECTED);
        setError(null);
        reconnectAttempt.current = 0;

        // Dispatch the DOM event for WebSocket connection
        const connectEventData = { timestamp: Date.now() }; // You can add more details if needed
        window.dispatchEvent(new CustomEvent('websocket-connected', { detail: connectEventData }));
        WSLogger.log('WebSocketContext: Dispatching "websocket-connected" DOM event with details:', connectEventData);
        
        // Setup heartbeat monitoring
        setupHeartbeatMonitoring(ws);
        
        // Handle topic subscriptions
        handleSubscriptions(ws);
      };
      
      ws.onmessage = handleMessage;
      
      ws.onerror = (event) => {
        WSLogger.error('Connection error:', event);
        setError('Connection error. Please check your network and server status.');
      };
      
      ws.onclose = (event) => {
        WSLogger.log(`Connection closed: code=${event.code}, reason="${event.reason || 'No reason'}", clean=${event.wasClean}`);

        // Dispatch "server-disconnected" for all subscribed servers
        const currentSubscriptions = subscriptionsRef.current; // Use ref for latest state in closure
        const disconnectedServerIds = new Set();

        if (Array.isArray(currentSubscriptions)) {
          currentSubscriptions.forEach(topicPath => {
            try {
              const parts = topicPath.split('/');
              const topicName = parts[parts.length - 1]; // e.g., serverId_eventType_events
              const serverId = topicName.split('_')[0]; // Extract serverId
              if (serverId && serverId !== 'undefined' && !disconnectedServerIds.has(serverId)) {
                const serverDisconnectEventData = {
                  serverId,
                  reason: `WebSocket connection closed (code: ${event.code}, reason: ${event.reason || 'N/A'})`,
                  wasClean: event.wasClean,
                  timestamp: Date.now()
                };
                window.dispatchEvent(new CustomEvent('server-disconnected', { detail: serverDisconnectEventData }));
                WSLogger.log(`WebSocketContext: Dispatched "server-disconnected" for ${serverId} due to WebSocket closure.`);
                disconnectedServerIds.add(serverId);
              }
            } catch (e) {
              WSLogger.error(`Error parsing serverId from topic path "${topicPath}" during onclose:`, e);
            }
          });
        } else {
          WSLogger.warn('subscriptionsRef.current is not an array or is null during onclose, cannot dispatch server-disconnected events.');
        }
        
        // Clean up resources
        cleanupConnection();

        // Dispatch a custom DOM event for WebSocketEventListener and other listeners
        const disconnectEventData = { code: event.code, reason: event.reason, wasClean: event.wasClean };
        WSLogger.debug('Dispatching "websocket-disconnected" DOM event with details:', disconnectEventData);
        window.dispatchEvent(new CustomEvent('websocket-disconnected', { detail: disconnectEventData }));
        
        if (intentionalClose.current) {
          WSLogger.log('Connection closed intentionally');
          setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
          return;
        }
        
        if (event.code === 1000) {
          // Normal closure
          setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
        } else {
          // Abnormal closure
          setConnectionStatus(CONNECTION_STATUS.ERROR);
          setError(`Connection closed abnormally (code ${event.code})`);
          
          // Schedule reconnection with exponential backoff
          const reconnectDelay = WSUtils.calculateBackoff(reconnectAttempt.current++);
          
          WSLogger.log(`Scheduling reconnection attempt ${reconnectAttempt.current} in ${Math.round(reconnectDelay/1000)}s`);
          
          reconnectTimer.current = setTimeout(() => {
            if (wsConnectionRef.current) {
              WSLogger.log('Attempting to reconnect...');
              initializeConnection();
            }
          }, reconnectDelay);
        }
      };
      
      // Store the socket reference
      setSocket(ws);
      
    } catch (err) {
      WSLogger.error('Error establishing connection:', err);
      setConnectionStatus(CONNECTION_STATUS.ERROR);
      setError(`Failed to connect: ${err.message}`);
      
      // Schedule reconnection
      const reconnectDelay = WSUtils.calculateBackoff(reconnectAttempt.current++);
      WSLogger.log(`Scheduling reconnection attempt ${reconnectAttempt.current} in ${Math.round(reconnectDelay/1000)}s`);
      
      reconnectTimer.current = setTimeout(() => {
        if (wsConnectionRef.current) {
          WSLogger.log('Attempting to reconnect...');
          initializeConnection();
        }
      }, reconnectDelay);
    }
  }, [
    wsConnection, 
    isAuthenticated, 
    cleanupConnection, 
    setupHeartbeatMonitoring, 
    handleSubscriptions, 
    handleMessage
  ]);
  
  // Main connection function
  const connect = useCallback(() => {
    // Validate connection information
    const validatedConnection = ConnectionService.validateConnectionInfo(wsConnection);
    
    if (!validatedConnection) {
      WSLogger.error('Invalid connection information');
      setConnectionStatus(CONNECTION_STATUS.ERROR);
      setError('Invalid WebSocket connection information');
      return;
    }
    
    if (!isAuthenticated) {
      WSLogger.error('Not authenticated');
      setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
      return;
    }
    
    // Reset reconnection attempts if this is a manual connection
    reconnectAttempt.current = 0;
    
    // Skip if already connected
    if (socket && socket.readyState === WebSocket.OPEN) {
      WSLogger.log('Already connected');
      setConnectionStatus(CONNECTION_STATUS.CONNECTED);
      return;
    }
    
    // Clean up any existing connection
    cleanupConnection();
    
    // Close any existing socket
    if (socket) {
      intentionalClose.current = true;
      try {
        WSLogger.warn('Closing existing connection before reconnecting');
        socket.close(1000, 'Normal closure initiated by client before reconnect');
      } catch (err) {
        WSLogger.warn('Error closing existing socket:', err);
      }
    }
    
    // Start the new connection process
    initializeConnection();
  }, [wsConnection, isAuthenticated, socket, cleanupConnection, initializeConnection]);

  // Function to disconnect WebSocket
  const disconnect = useCallback(() => {
    WSLogger.log('Disconnecting WebSocket...');
    
    // Mark as intentional close
    intentionalClose.current = true;
    
    // Clean up all timers
    cleanupConnection();
    
    // Close the socket if it exists
    if (socket) {
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        try {
          socket.close(1000, 'Disconnect requested by user');
        } catch (err) {
          WSLogger.error('Error closing socket:', err);
        }
      }
    }
    
    // Reset state
    setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
    setError(null);
    reconnectAttempt.current = 0;
  }, [socket, cleanupConnection]);
  
  // Effect to clean up on unmount
  useEffect(() => {
    return () => {
      intentionalClose.current = true;
      cleanupConnection();
      
      if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
        try {
          socket.close(1000, 'Component unmounting');
        } catch (err) {
          WSLogger.error('Error closing socket during cleanup:', err);
        }
      }
    };
  }, [socket, cleanupConnection]);
  
  // Reset subscription state (useful when changing servers)
  const resetSubscriptions = useCallback(() => {
    hasSubscribed.current = false;
    setSubscriptions([]);
    if (socket && socket.readyState === WebSocket.OPEN) {
      handleSubscriptions(socket);
    }
  }, [socket, handleSubscriptions]);

  // Function to manually subscribe to a specific topic if needed
  // Note: This is typically not needed as subscription is automatic during connection
  const subscribe = useCallback((topic) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      console.error('Cannot subscribe: WebSocket not connected');
      return false;
    }
    
    try {
      // Extract topic name from full topic path (persistent://public/default/topic_name)
      const topicName = topic.split('/').pop();
      
      // Parse the topic name to get server_id and event_type
      // Format: {server_id}_{event_type}_events
      const parts = topicName.split('_');
      if (parts.length >= 3 && parts[parts.length-1] === 'events') {
        const serverId = parts[0];
        const eventType = parts[1];
        
        console.log(`WebSocketContext: Manual subscription requested for ${serverId}_${eventType}`);
        
        // Check if we're already subscribed to this topic
        const fullTopic = `persistent://public/default/${topicName}`;
        if (subscriptions.includes(fullTopic)) {
          console.log(`WebSocketContext: Already subscribed to ${fullTopic}`);
          return true;
        }
        
        // If not already subscribed, trigger a refresh of all subscriptions
        resetSubscriptions();
        return true;
      } else {
        console.error(`Invalid topic name format: ${topicName}. Expected format: {server_id}_{event_type}_events`);
        return false;
      }
    } catch (err) {
      console.error('Error processing subscription request:', err);
      return false;
    }
  }, [socket, subscriptions, resetSubscriptions]);
  
  // Legacy function maintained for backward compatibility
  // Note: This is typically not needed as subscription is automatic during connection
  const subscribeToServerTopics = useCallback((serverId, eventTypes = ['systemevent', 'process', 'log', 'heartbeat']) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      console.error('WebSocketContext: Cannot subscribe - WebSocket not connected');
      return false;
    }
    
    try {
      console.log(`WebSocketContext: Manual server subscription requested for server ${serverId}`);
      console.log('Note: Manual subscription should not be necessary as subscription is automatic during connection');
      
      // Check if we already have subscriptions for this server
      const normalizedServerId = serverId.toLowerCase();
      const hasExistingSubscriptions = subscriptions.some(topicName => {
        return topicName.startsWith(normalizedServerId + '_');
      });
      
      if (hasExistingSubscriptions) {
        console.log(`WebSocketContext: Already have subscriptions for server ${normalizedServerId}`);
        return true;
      }
      
      // If not already subscribed, trigger a refresh of all subscriptions
      resetSubscriptions();
      return true;
    } catch (err) {
      console.error(`WebSocketContext: Error processing server subscription request for ${serverId}:`, err);
      return false;
    }
  }, [socket, subscriptions, resetSubscriptions]);
  
  // Connect when auth status changes
  useEffect(() => {
    let connectionTimer = null;
    
    // Only connect if we have authentication and connection info
    if (isAuthenticated && wsConnection) {
      // Only attempt to connect if we're in a disconnected or error state
      // This prevents reconnection loops
      if (connectionStatus === CONNECTION_STATUS.DISCONNECTED || 
          connectionStatus === CONNECTION_STATUS.ERROR) {
        console.log('WebSocketContext: Connection info changed or initial connection, attempting connection in 2 seconds...');
        connectionTimer = setTimeout(() => {
          connect();
        }, 2000);
      }
    } else if (!isAuthenticated || !wsConnection) {
      disconnect();
    }
    
    // Clean up on unmount or when dependencies change
    return () => {
      if (connectionTimer) {
        clearTimeout(connectionTimer);
      }
    };
  }, [isAuthenticated, wsConnection, connectionStatus, connect, disconnect]);
  
  // Function to get messages for a specific server
  const getServerMessages = useCallback((serverId) => {
    if (!serverId) return null;
    return serverMessages[serverId.toLowerCase()] || null;
  }, [serverMessages]);
  
  // Function to get the latest message of a specific type for a server
  const getServerMessageByType = useCallback((serverId, eventType) => {
    if (!serverId || !eventType) return null;
    const server = serverMessages[serverId.toLowerCase()];
    if (!server) return null;
    return server[eventType] || null;
  }, [serverMessages]);
  
  // Get list of all server IDs that have messages
  const getActiveServerIds = useCallback(() => {
    return Object.keys(serverMessages);
  }, [serverMessages]);
  
  // Check if a server has sent a message recently (within the last 3 minutes)
  const isServerActive = useCallback((serverId) => {
    if (!serverId) return false;
    const server = serverMessages[serverId.toLowerCase()];
    if (!server) return false;
    
    // Check if any message was received in the last 3 minutes
    const currentTime = Date.now();
    const maxInactiveTime = 3 * 60 * 1000; // 3 minutes
    
    return Object.values(server).some(msg => {
      // Ensure msg and msg.timestamp are valid before comparison
      return msg && typeof msg === 'object' && msg.timestamp && (currentTime - msg.timestamp < maxInactiveTime);
    });
  }, [serverMessages]);

  // Provide context value
  const value = {
    // Connection state
    connectionStatus,
    error,
    
    // Messages 
    lastMessage,         // Latest message (for backward compatibility)
    serverMessages,      // All messages organized by server ID
    
    // Subscription state
    subscriptions,       // List of subscribed topics
    
    // Connection functions
    connect,             // Connect to WebSocket
    disconnect,          // Disconnect WebSocket
    
    // Subscription functions
    subscribe,           // Subscribe to a specific topic
    subscribeToServerTopics, // Subscribe to all topics for a server
    resetSubscriptions,  // Reset subscription state
    
    // Server-specific helper functions
    getServerMessages,      // Get all messages for a specific server
    getServerMessageByType, // Get specific message type for a server
    getActiveServerIds,     // Get list of active server IDs
    isServerActive,         // Check if a server is active
  };
  
  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}
